import {
  BWellSDK,
  CreateConsentRequest,
  CreateVerificationUrlRequest,
} from "@icanbwell/bwell-sdk-ts";
import React from "react";

import { Api } from "@noom/noomscape";

import { getAutologinData } from "@/utils/cookies";

/**
 * Integration with the BWell API
 * See: https://coda.io/d/Tech-Overview-Documents_duOVNii207j/b-Well-Integration-Flow_sulUN51r#_lu-sm-lz
 */

export const useBWell = () => {
  const [isLoading, setIsLoading] = React.useState(false);

  const initialize = async (): Promise<BWellSDK> => {
    const clientKey = process.env.HIVE_BWELL_CLIENT_KEY;
    if (!clientKey) {
      throw new Error(
        "BWell client key is not defined in environment variables.",
      );
    }
    try {
      const sdk = new BWellSDK({ clientKey });
      await sdk.initialize();
      return sdk;
    } catch (error) {
      throw new Error(
        `Failed to initialize BWell SDK: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    }
  };

  const getIdentityToken = async (): Promise<string> => {
    const autologinData = getAutologinData();
    if (!autologinData?.accessToken || !autologinData?.accessCode) {
      throw new Error(
        "No access token or access code available for BWell authentication.",
      );
    }
    try {
      const { token } = await Api.call(
        "identity.mintPartnerAssertion",
        Api.api.identity.mintPartnerAssertion,
        {
          partner: "B_WELL",
          accessCode: autologinData.accessCode,
          accessToken: autologinData.accessToken,
        },
      );
      return token;
    } catch (error) {
      throw new Error(
        `Failed to get token from identity server: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    }
  };

  const authenticate = async (sdk: BWellSDK) => {
    const token = await getIdentityToken();
    try {
      await sdk.authenticate({ token });
    } catch (error) {
      throw new Error(
        `Failed to authenticate with BWell SDK: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    }
  };

  /**
   * Launches the CLEAR identity verification flow.
   * Obtains a verification URL from b.Well and redirects the user to it.
   * After the user completes the flow, they will be redirected back to the
   * app via the callback URL, where we do part two of the process and
   * call createIasConsent.
   */
  const launchIdentityVerification = async () => {
    setIsLoading(true);
    const callbackUrl = `${window.location.origin}/connect-health-records/connectHealthRecordsConsent`;
    try {
      const sdk = await initialize();
      await authenticate(sdk);

      const tosResult = await sdk.user.createConsent(
        new CreateConsentRequest({
          status: "ACTIVE",
          provision: "PERMIT",
          category: "TOS",
        }),
      );
      if (!tosResult.success()) {
        throw new Error(
          `createConsent (TOS) request failed: ${tosResult.error().message}`,
        );
      }

      const result = await sdk.user.createVerificationUrl(
        new CreateVerificationUrlRequest({ callbackUrl }),
      );
      if (result.success()) {
        const verificationUrl = result.data();
        window.location.href = verificationUrl;
      } else {
        throw new Error(
          `createVerificationUrl request failed: ${result.error().message}`,
        );
      }
    } catch (error) {
      throw new Error(
        `Error launching identity verification: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Notifies b.Well that the user provided consent for importing records.
   * This will trigger the data import on the b.Well side. Our backend will
   * startg polling for the status of the import.
   */
  const createIasConsent = async () => {
    setIsLoading(true);
    try {
      const autologinData = getAutologinData();
      if (!autologinData?.accessToken || !autologinData?.accessCode) {
        throw new Error(
          "No access token or access code available for health records retrieval.",
        );
      }
      const sdk = await initialize();
      await authenticate(sdk);

      // create consent in b.Well's system and kick off records retrieval
      const result = await sdk.user.createConsent(
        new CreateConsentRequest({
          status: "ACTIVE",
          provision: "PERMIT",
          category: "IAS_IMPORT_RECORDS",
        }),
      );
      if (result.success()) {
        // notify backend to start polling for status
        await Api.call(
          "healthRecords.scheduleHealthRecordsRetrieval",
          Api.api.healthRecords.scheduleHealthRecordsRetrieval,
          {
            accessCode: autologinData.accessCode,
            accessToken: autologinData.accessToken,
          },
        );
      } else {
        throw new Error(
          `createConsent request failed: ${result.error().message}`,
        );
      }
    } catch (error) {
      throw new Error(
        `Error creating IAS consent: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    launchIdentityVerification,
    createIasConsent,
    isLoading,
  };
};
